import { useState, useMemo, useEffect } from 'react';
import { Form } from 'antd';
import { ProductType } from '../../../utils/fbaFeeData';
import { DEFAULT_VALUES } from '../constants/pricingConstants';
import { usePricingData } from './usePricingData';
import { useFBAFeeCalculation } from './useFBAFeeCalculation';
import { useChannelShipping } from './useChannelShipping';
import { usePriceMarginSync } from './usePriceMarginSync';
import {
    checkPeakSeason,
    calculateVolumeWeight,
    calculateChargeableWeight,
    calculateLandedCost,
    calculateChannelResults,
    findBestChannel
} from '../utils/pricingCalculations';

export const usePricingLogic = (externalForm?: any) => {
    const internalForm = Form.useFormInstance();
    const form = externalForm || internalForm;

    // Form Watchers
    const categoryFromBasic = Form.useWatch('category', form);
    const purchaseCostFromBasic = Form.useWatch('purchase_cost_cny', form);
    const price = Form.useWatch('price', form);
    const profitMargin = Form.useWatch('profit_margin', form);
    const weight = Number(Form.useWatch('weight', form)) || 0;
    const length = Number(Form.useWatch('length', form)) || 0;
    const width = Number(Form.useWatch('width', form)) || 0;
    const height = Number(Form.useWatch('height', form)) || 0;

    // State
    const [site, setSite] = useState<string>(DEFAULT_VALUES.SITE);
    const [purchaseCostCNY, setPurchaseCostCNY] = useState<number>(0);
    const [isPeakSeason, setIsPeakSeason] = useState<boolean>(checkPeakSeason());
    const [selectedChannelId, setSelectedChannelId] = useState<string>('');
    const [lastChanged, setLastChanged] = useState<'price' | 'margin'>('margin');
    const [productType, setProductType] = useState<ProductType>('standard');
    const [dutyRate, setDutyRate] = useState<number>(DEFAULT_VALUES.DUTY_RATE);
    const [returnRate, setReturnRate] = useState<number>(DEFAULT_VALUES.RETURN_RATE);
    const [otherCostRate, setOtherCostRate] = useState<number>(DEFAULT_VALUES.OTHER_COST_RATE);

    // Data Loading Hook
    const pricingData = usePricingData(site);
    const { exchangeRate, referralRate, channels } = pricingData;

    // Derived Calculations
    const volumeWeight = useMemo(() => calculateVolumeWeight(length, width, height), [length, width, height]);
    const chargeableWeight = useMemo(() => calculateChargeableWeight(weight, volumeWeight), [weight, volumeWeight]);

    // Shipping Hook
    const { shippingCostCNY, selectedChannelRateInfo } = useChannelShipping(
        channels,
        selectedChannelId,
        chargeableWeight
    );

    // FBA Fee Hook
    const fbaData = useFBAFeeCalculation(
        site, length, width, height, weight, price || 0, productType, isPeakSeason
    );
    const { fbaFee, currentStorageFee } = fbaData;

    // Landed Cost Calculation
    const landedCost = useMemo(() => calculateLandedCost(
        purchaseCostCNY,
        shippingCostCNY,
        exchangeRate,
        dutyRate
    ), [purchaseCostCNY, shippingCostCNY, dutyRate, exchangeRate]);

    // Shipping Cost USD - for marketing activities
    const shippingCostUSD = useMemo(() =>
        exchangeRate > 0 ? shippingCostCNY / exchangeRate : 0
        , [shippingCostCNY, exchangeRate]);

    // Price/Margin Sync Hook
    usePriceMarginSync(
        form,
        landedCost,
        fbaFee,
        currentStorageFee,
        referralRate,
        returnRate,
        otherCostRate,
        lastChanged,
        price,
        profitMargin
    );

    // Effects
    useEffect(() => {
        if (purchaseCostFromBasic) {
            setPurchaseCostCNY(Number(purchaseCostFromBasic));
        }
    }, [purchaseCostFromBasic]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (categoryFromBasic) {
            pricingData.loadCategoryReferralRate(categoryFromBasic);
        }
    }, [categoryFromBasic]);

    // 加载物流渠道和汇率数据（只在 site 改变时）
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        pricingData.loadChannels();
        pricingData.loadExchangeRate();
    }, [site]); // 只依赖 site，避免无限循环

    // 初始化默认利润率
    useEffect(() => {
        // 只有当利润率未定义且价格也未定义时，才设置默认值
        // 这样可以避免覆盖用户已有的输入
        const currentMargin = form.getFieldValue('profit_margin');
        const currentPrice = form.getFieldValue('price');

        if (currentMargin === undefined && currentPrice === undefined) {
            form.setFieldsValue({
                profit_margin: DEFAULT_VALUES.PROFIT_MARGIN
            });
        }
    }, [form]);

    // Channel Results
    const channelResults = useMemo(() => calculateChannelResults(
        channels,
        chargeableWeight,
        exchangeRate,
        purchaseCostCNY,
        dutyRate,
        price || 0,
        fbaFee,
        referralRate,
        currentStorageFee,
        returnRate,
        otherCostRate
    ), [channels, chargeableWeight, purchaseCostCNY, dutyRate, exchangeRate, fbaFee, referralRate, currentStorageFee, returnRate, otherCostRate, price]);

    const bestChannel = useMemo(() => findBestChannel(channelResults), [channelResults]);

    useEffect(() => {
        if (bestChannel && !selectedChannelId) {
            setSelectedChannelId(bestChannel.key);
        }
    }, [bestChannel, selectedChannelId]);

    return {
        form,
        state: {
            site, setSite,
            purchaseCostCNY, setPurchaseCostCNY,
            isPeakSeason, setIsPeakSeason,
            selectedChannelId, setSelectedChannelId,
            lastChanged, setLastChanged,
            productType, setProductType,
            dutyRate, setDutyRate,
            returnRate, setReturnRate,
            otherCostRate, setOtherCostRate,
            categoryFromBasic,
        },
        data: {
            ...pricingData,
            volumeWeight,
            chargeableWeight,
            shippingCostCNY,
            shippingCostUSD,
            selectedChannelRateInfo,
            ...fbaData,
            landedCost,
            channelResults,
            bestChannel,
        }
    };
};
